import apiService from "./apiService";
import { ENDPOINTS } from "../Endpoints";
import { resolveAssistantBookingAvailability } from "../../utils/assistantAvailability";

const resolveFullName = (assistant = {}) => {
  const user = assistant.user || {};
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name.trim() || "";
};

const normalizeLanguages = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    return value
      .split(",")
      .map((lang) => lang.trim())
      .filter(Boolean);
  }
  return [];
};

const formatPrice = (value, currency = "") => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `${currency}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeText = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeLocationValue = (value) => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";

  const city =
    normalizeText(value.city) ||
    normalizeText(value.town) ||
    normalizeText(value.localGovernment) ||
    normalizeText(value.local_government);
  const state =
    normalizeText(value.state) ||
    normalizeText(value.region) ||
    normalizeText(value.province) ||
    normalizeText(value.based);
  const label =
    normalizeText(value.label) ||
    normalizeText(value.name) ||
    normalizeText(value.address) ||
    normalizeText(value.formattedAddress) ||
    normalizeText(value.formatted_address);

  if (city && state) return `${city}, ${state}`;
  return label || city || state || "";
};

const resolveLocation = (assistant = {}) => {
  const user = assistant.user || {};
  const candidates = [
    assistant.location,
    assistant.profile?.location,
    assistant.address,
    assistant.address?.state,
    assistant.address?.city,
    assistant.state,
    assistant.city,
    assistant.profile?.state,
    assistant.profile?.city,
    user.location,
    user.state,
    user.city,
  ];

  for (const candidate of candidates) {
    const resolved = normalizeLocationValue(candidate);
    if (resolved) return resolved;
  }

  return "";
};

const resolveCompletedBookings = (assistant = {}) => {
  const raw =
    assistant.completedBookings ??
    assistant.bookingMetrics?.completedBookings ??
    assistant.metrics?.completedBookings ??
    assistant.stats?.completedBookings ??
    assistant.totalCompletedBookings ??
    assistant.total_completed_bookings ??
    0;
  const count = Number(raw);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const normalizeAssistantListItem = (assistant = {}) => {
  const profilePictureUrl = assistant.profilePicture?.url || "";
  const languages = normalizeLanguages(assistant.languages);
  const experienceYears = assistant.experienceYears ?? null;
  const pricePerDay = assistant.pricePerDay ?? 0;

  return {
    id: assistant.user?._id || "",
    name: resolveFullName(assistant),
    rating: assistant.rating ?? 0,
    completedBookings: resolveCompletedBookings(assistant),
    location: resolveLocation(assistant),
    languages: languages.join(", "),
    image: profilePictureUrl,
    profilePicture: { url: profilePictureUrl },
    bio: assistant.bio || "",
    experienceYears:
      Number.isFinite(Number(experienceYears)) && Number(experienceYears) > 0
        ? Number(experienceYears)
        : null,
    services: Array.isArray(assistant.services) ? assistant.services : [],
    specializations: Array.isArray(assistant.specializations)
      ? assistant.specializations
      : [],
    pricePerDay,
    formattedPrice: formatPrice(pricePerDay),
    isAvailable: resolveAssistantBookingAvailability(assistant),
    schedule: { availability: assistant.availability || "" },
    vehicleDescription: assistant.vehicleDescription || "",
  };
};

class AssistantsService {
  async getAssistants(params = {}) {
    return apiService.get(ENDPOINTS.ASSISTANTS.LIST, params);
  }

  async getAssistantById(id, params = {}) {
    if (!id) {
      throw new Error("Assistant ID is required.");
    }
    return apiService.get(`${ENDPOINTS.ASSISTANTS.DETAIL}/${id}`, params);
  }

  // Busy windows for one assistant over [from, to). Public — the calendar has
  // to render before a visitor signs in.
  async getAssistantAvailability(id, { from, to } = {}) {
    if (!id) {
      throw new Error("Assistant ID is required.");
    }
    if (!from || !to) {
      throw new Error("A from and to date are required.");
    }
    return apiService.get(`${ENDPOINTS.ASSISTANTS.AVAILABILITY}/${id}/availability`, {
      from: new Date(from).toISOString(),
      to: new Date(to).toISOString(),
    });
  }

  async getRelatedAssistants(id, params = {}) {
    if (!id) {
      throw new Error("Assistant ID is required.");
    }
    return apiService.get(
      `${ENDPOINTS.ASSISTANTS.RELATED}/${id}/related`,
      params
    );
  }
}

const assistantsService = new AssistantsService();

export default assistantsService;
